import { useEffect } from "react";
import { SITE_URL, CLINIC_LOGO_URL } from "@/components/schemaConstants";

/**
 * Sitewide JSON-LD @graph: MedicalClinic (+ MedicalOrganization + LocalBusiness)
 * + MedicalBusiness + WebSite + the homepage MedicalWebPage.
 *
 * Injected once from the app root as a single <script type="application/ld+
 * json"> in <body>. Page-level schema (MedicalTherapy, FAQPage, Article,
 * BreadcrumbList, per-route MedicalWebPage) is emitted from each route's
 * `head().scripts` and links back to these entities by @id.
 *
 * NAP data is constant and matches the visible footer/nav: address 9734 West
 * Sample Road, 2nd Floor, Suite B, Coral Springs, FL 33065; phone
 * 954-827-9793; sameAs Google Business Profile + socials.
 *
 * No invented stats, reviews, or pricing: priceRange/currenciesAccepted/
 * paymentAccepted reflect "contact for pricing" and standard US payment
 * methods; availableService links to the live SAINT/TMS/Spravato pages.
 */
const CLINIC_NAME = "Neuro Wellness TMS Centers of America";

const CLINIC_ADDRESS = {
  "@type": "PostalAddress",
  streetAddress: "9734 West Sample Road, 2nd Floor, Suite B",
  addressLocality: "Coral Springs",
  addressRegion: "FL",
  postalCode: "33065",
  addressCountry: "US",
};

const CLINIC_GEO = {
  "@type": "GeoCoordinates",
  latitude: 26.2723,
  longitude: -80.2469,
};

const CLINIC_HOURS = [
  {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    opens: "08:00",
    closes: "18:00",
  },
];

const CLINIC_SAME_AS = [
  "https://www.google.com/maps/place/Neuro+Wellness+TMS+Centers+of+America/@26.2723,-80.2469,15z",
  "https://www.facebook.com/neurowellnesstms",
  "https://www.instagram.com/neurowellnesstms",
  "https://www.youtube.com/@neurowellnesstms",
];

const sitewideGraph = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["MedicalClinic", "MedicalOrganization", "LocalBusiness"],
      "@id": `${SITE_URL}/#organization`,
      name: CLINIC_NAME,
      description:
        "Outpatient psychiatry clinic specializing in SAINT® accelerated TMS and Spravato® for treatment-resistant depression in Coral Springs, Florida.",
      url: SITE_URL,
      telephone: "+1-954-827-9793",
      email: "care@neurowellnesstms.com",
      logo: {
        "@type": "ImageObject",
        url: CLINIC_LOGO_URL,
      },
      image: {
        "@type": "ImageObject",
        url: CLINIC_LOGO_URL,
      },
      address: CLINIC_ADDRESS,
      geo: CLINIC_GEO,
      openingHoursSpecification: CLINIC_HOURS,
      medicalSpecialty: "Psychiatry",
      areaServed: [
        { "@type": "State", name: "Florida" },
        { "@type": "Country", name: "United States" },
      ],
      priceRange: "Contact for pricing",
      currenciesAccepted: "USD",
      paymentAccepted: "Cash, Credit Card, Insurance",
      knowsAbout: [
        "Transcranial magnetic stimulation",
        "Accelerated TMS",
        "Stanford Accelerated Intelligent Neuromodulation Therapy",
        "Treatment-resistant depression",
        "Esketamine",
        "Spravato",
        "Major depressive disorder",
        "Psychiatry",
      ],
      availableService: [
        {
          "@type": "MedicalTherapy",
          name: "SAINT® Accelerated TMS",
          url: `${SITE_URL}/saint`,
        },
        {
          "@type": "MedicalTherapy",
          name: "Transcranial Magnetic Stimulation (TMS)",
          url: `${SITE_URL}/services/tms`,
        },
        {
          "@type": "MedicalTherapy",
          name: "Spravato® (Esketamine)",
          url: `${SITE_URL}/services/spravato`,
        },
      ],
      sameAs: CLINIC_SAME_AS,
    },
    {
      "@type": "MedicalBusiness",
      "@id": `${SITE_URL}/#medicalbusiness`,
      name: CLINIC_NAME,
      url: SITE_URL,
      telephone: "+1-954-827-9793",
      address: CLINIC_ADDRESS,
      parentOrganization: { "@id": `${SITE_URL}/#organization` },
    },
    {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      name: CLINIC_NAME,
      url: SITE_URL,
      publisher: { "@id": `${SITE_URL}/#organization` },
      inLanguage: "en-US",
    },
    {
      "@type": "MedicalWebPage",
      "@id": `${SITE_URL}/#webpage`,
      name: `${CLINIC_NAME} — SAINT® TMS for Treatment-Resistant Depression`,
      url: SITE_URL,
      isPartOf: { "@id": `${SITE_URL}/#website` },
      about: { "@id": `${SITE_URL}/#organization` },
      inLanguage: "en-US",
      provider: { "@id": `${SITE_URL}/#organization` },
    },
  ],
};

/** Deterministic, stable script id so SSR and CSR render the same markup. */
const SCRIPT_ID = "sitewide-jsonld";

/**
 * Injects the sitewide MedicalClinic @graph as a single JSON-LD script.
 * Renders server-side (the <script> tag is emitted during the first render)
 * so crawlers receive it without waiting for hydration.
 */
export default function SitewideSchema() {
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (document.getElementById(SCRIPT_ID)) return;
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.type = "application/ld+json";
    script.text = JSON.stringify(sitewideGraph);
    document.body.appendChild(script);
    return () => {
      document.getElementById(SCRIPT_ID)?.remove();
    };
  }, []);

  // Also emit server-rendered markup so crawlers see it pre-hydration.
  return (
    <script
      id={SCRIPT_ID}
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(sitewideGraph) }}
    />
  );
}
